// Cálculo de puntuación de una propiedad para la vista de aprobación.
// Combina COMPLETITUD (campos obligatorios cargados) y CALIDAD de la
// publicación (fotos, descripción, documentación, video, ubicación, comisiones).
// Devuelve un score 0-100, un desglose por ítem y la lista de obligatorios
// faltantes para que la martillera sepa exactamente qué corregir.

import { isTruthy, isLandType, isValidYoutubeUrl } from './propertyLabels';

const hasValue = (v) => {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') return v.trim() !== '';
  return true;
};

const isPositiveNumber = (v) => {
  if (!hasValue(v)) return false;
  const n = Number(v);
  return !Number.isNaN(n) && n >= 0;
};

// Campos obligatorios base (siempre) y sus etiquetas legibles.
const BASE_REQUIRED = [
  { field: 'title', label: 'Título' },
  { field: 'description', label: 'Descripción' },
  { field: 'price', label: 'Precio' },
  { field: 'propertyType', label: 'Tipo de propiedad' },
  { field: 'propertyState', label: 'Estado de la propiedad' },
  { field: 'sellerCommission', label: 'Comisión del vendedor' },
  { field: 'buyerCommission', label: 'Comisión del comprador' },
  { field: 'street', label: 'Calle' },
  { field: 'number', label: 'Número' },
  { field: 'country', label: 'País' },
  { field: 'province', label: 'Provincia' },
  { field: 'departments', label: 'Departamento' },
  { field: 'locality', label: 'Localidad' },
  { field: 'land', label: 'Superficie del terreno' },
];

// Obligatorios extra cuando NO es terreno/campo.
const NON_LAND_REQUIRED = [
  { field: 'environments', label: 'Ambientes' },
  { field: 'rooms', label: 'Dormitorios' },
  { field: 'bathrooms', label: 'Baños' },
  { field: 'toilettes', label: 'Toilettes' },
  { field: 'garages', label: 'Cocheras' },
  { field: 'floorPlans', label: 'Pisos' },
  { field: 'coveredSquareMeters', label: 'Superficie cubierta' },
  { field: 'uncovered', label: 'Superficie descubierta' },
];

// Devuelve la lista de campos obligatorios (según tipo) evaluando faltantes.
export const getRequiredFields = (property) => {
  if (!property) return [];
  const land = isLandType(property.propertyType);
  const required = [...BASE_REQUIRED, ...(land ? [] : NON_LAND_REQUIRED)];
  const operationOk =
    isTruthy(property.isForSale) || isTruthy(property.isForRent);

  const evaluated = required.map((r) => ({
    ...r,
    ok: isPositiveNumericField(r.field)
      ? isPositiveNumber(property[r.field])
      : hasValue(property[r.field]),
  }));

  evaluated.push({
    field: 'operation',
    label: 'Tipo de operación (Venta / Alquiler)',
    ok: operationOk,
  });

  return evaluated;
};

const NUMERIC_FIELDS = new Set([
  'price',
  'land',
  'environments',
  'rooms',
  'bathrooms',
  'toilettes',
  'garages',
  'floorPlans',
  'coveredSquareMeters',
  'uncovered',
  'sellerCommission',
  'buyerCommission',
]);

function isPositiveNumericField(field) {
  return NUMERIC_FIELDS.has(field);
}

// Calcula la puntuación completa.
export const computePropertyScore = (property) => {
  if (!property) {
    return { score: 0, breakdown: [], missingRequired: [], requiredFields: [] };
  }

  const requiredFields = getRequiredFields(property);
  const missingRequired = requiredFields.filter((r) => !r.ok);

  const photos = Array.isArray(property.photo) ? property.photo : [];
  const docs = Array.isArray(property.documentation)
    ? property.documentation
    : [];
  const descLen = (property.description || '').trim().length;
  const location = Array.isArray(property.location) ? property.location : [];
  const hasValidLocation =
    location.length === 2 &&
    hasValue(location[0]) &&
    hasValue(location[1]) &&
    !Number.isNaN(Number(location[0])) &&
    !Number.isNaN(Number(location[1]));

  const breakdown = [];

  // 1. Campos obligatorios completos (40 pts) — proporcional.
  const totalReq = requiredFields.length;
  const okReq = requiredFields.filter((r) => r.ok).length;
  const reqPoints = totalReq > 0 ? Math.round((okReq / totalReq) * 40) : 40;
  breakdown.push({
    key: 'required',
    label: 'Datos obligatorios completos',
    points: reqPoints,
    max: 40,
    ok: okReq === totalReq,
    hint:
      okReq === totalReq
        ? 'Todos los datos obligatorios están cargados.'
        : `Faltan ${totalReq - okReq} de ${totalReq} datos obligatorios.`,
  });

  // 2. Fotos (20 pts): 0 fotos = 0; 1-4 = parcial; >=5 = completo.
  let photoPoints = 0;
  let photoHint = 'Sin fotos cargadas.';
  if (photos.length >= 5) {
    photoPoints = 20;
    photoHint = `${photos.length} fotos cargadas.`;
  } else if (photos.length >= 1) {
    photoPoints = Math.round((photos.length / 5) * 20);
    photoHint = `${photos.length} foto(s). Se recomiendan al menos 5.`;
  }
  breakdown.push({
    key: 'photos',
    label: 'Fotografías',
    points: photoPoints,
    max: 20,
    ok: photos.length >= 5,
    hint: photoHint,
  });

  // 3. Descripción (15 pts): según longitud.
  let descPoints = 0;
  let descHint = 'Sin descripción.';
  if (descLen >= 250) {
    descPoints = 15;
    descHint = 'Descripción completa.';
  } else if (descLen >= 120) {
    descPoints = 10;
    descHint = 'Descripción aceptable, podría ampliarse.';
  } else if (descLen > 0) {
    descPoints = 5;
    descHint = 'Descripción demasiado corta.';
  }
  breakdown.push({
    key: 'description',
    label: 'Descripción',
    points: descPoints,
    max: 15,
    ok: descLen >= 250,
    hint: descHint,
  });

  // 4. Documentación (10 pts): al menos 1 documento.
  breakdown.push({
    key: 'documentation',
    label: 'Documentación adjunta',
    points: docs.length > 0 ? 10 : 0,
    max: 10,
    ok: docs.length > 0,
    hint:
      docs.length > 0
        ? `${docs.length} documento(s) adjunto(s).`
        : 'No hay documentación adjunta.',
  });

  // 5. Ubicación / mapa (7 pts).
  breakdown.push({
    key: 'location',
    label: 'Ubicación en el mapa',
    points: hasValidLocation ? 7 : 0,
    max: 7,
    ok: hasValidLocation,
    hint: hasValidLocation
      ? 'Coordenadas cargadas.'
      : 'Falta ubicar la propiedad en el mapa.',
  });

  // 6. Video (5 pts).
  const hasVideo = isValidYoutubeUrl(property.videoLink);
  breakdown.push({
    key: 'video',
    label: 'Video de YouTube',
    points: hasVideo ? 5 : 0,
    max: 5,
    ok: hasVideo,
    hint: hasVideo ? 'Video cargado.' : 'Sin video (opcional pero suma).',
  });

  // 7. Comisiones cargadas (3 pts).
  const commissionsOk =
    isPositiveNumber(property.sellerCommission) &&
    isPositiveNumber(property.buyerCommission);
  breakdown.push({
    key: 'commissions',
    label: 'Comisiones definidas',
    points: commissionsOk ? 3 : 0,
    max: 3,
    ok: commissionsOk,
    hint: commissionsOk
      ? 'Comisiones cargadas.'
      : 'Faltan definir las comisiones.',
  });

  const score = Math.min(
    100,
    breakdown.reduce((acc, b) => acc + b.points, 0)
  );

  return { score, breakdown, missingRequired, requiredFields };
};

// Etiqueta cualitativa según el score.
export const getScoreLevel = (score) => {
  if (score >= 85) return { label: 'Lista para publicar', color: '#28a745' };
  if (score >= 60) return { label: 'Casi lista', color: '#f0ad4e' };
  if (score >= 35) return { label: 'Incompleta', color: '#fd7e14' };
  return { label: 'Muy incompleta', color: '#dc3545' };
};
